/**
 * Figma 转换：Figma REST API 的 file/nodes JSON → AppSchema 组件树。
 *
 * 输入（用户粘贴 JSON）接受三种形态：
 *   1. file 端点响应：{ name, document: { type: "CANVAS", children: [...] } }
 *   2. nodes 端点响应：{ nodes: { "1:2": { document: {...} } } }
 *   3. 裸节点树 / 节点数组
 *
 * 节点类型映射（未知/纯图形节点优雅降级为跳过并计数）：
 *   FRAME/SECTION/GROUP/INSTANCE/COMPONENT/COMPONENT_SET
 *     ├─ 名称含 button → Button
 *     ├─ 名称含 card   → Card
 *     ├─ auto-layout HORIZONTAL → Flex（direction row + gap/justify/align）
 *     └─ 其余（含 VERTICAL auto-layout）→ Container（direction/gap/padding）
 *   TEXT      → Text（content/fontSize/color/bold，tag 按字号分级）
 *   RECTANGLE → fills 含 IMAGE 时 → Image；否则按名称 button/card 或纯色 Container
 *   其余类型（ELLIPSE/VECTOR/LINE 等）→ 跳过并计数
 *
 * 样式尽量映射：SOLID fill → background（rgba）、cornerRadius → borderRadius、
 * width/height → style、auto-layout → 容器 props。
 */

import { v4 as uuidv4 } from "uuid";

import type { ComponentNode, StyleConfig } from "@shared/types";

// ---------------------------------------------------------------------------
// Figma REST API 节点子集
// ---------------------------------------------------------------------------

export interface FigmaColor {
  r: number;
  g: number;
  b: number;
  a?: number;
}

export interface FigmaFill {
  type: string;
  visible?: boolean;
  opacity?: number;
  color?: FigmaColor;
}

export interface FigmaNode {
  id?: string;
  name?: string;
  type: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  children?: FigmaNode[];
  characters?: string;
  fontSize?: number;
  fontWeight?: number;
  textAlignHorizontal?: string;
  fills?: FigmaFill[];
  cornerRadius?: number;
  rectangleCornerRadii?: number[];
  layoutMode?: "HORIZONTAL" | "VERTICAL";
  primaryAxisAlignItems?: "MIN" | "CENTER" | "MAX" | "SPACE_BETWEEN";
  counterAxisAlignItems?: "MIN" | "CENTER" | "MAX";
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
  itemSpacing?: number;
}

/** 转换统计：各组件计数 + 跳过数。 */
export interface FigmaImportStats {
  containers: number;
  texts: number;
  cards: number;
  images: number;
  buttons: number;
  skipped: number;
}

export interface FigmaImportResult {
  /** 根组件（Container，包裹全部顶层节点）。 */
  node: ComponentNode;
  /** 组件树统计（预览用）。 */
  stats: FigmaImportStats;
  /** 降级/提示信息（如图片节点需手动填充 src）。 */
  warnings: string[];
  /** UI 状态：是否已应用到画布（由调用方设置）。 */
  applied?: boolean;
}

// ---------------------------------------------------------------------------
// 输入归一化
// ---------------------------------------------------------------------------

/** 把三种形态的 Figma JSON 归一化为待转换的节点列表。 */
export function normalizeFigmaInput(json: unknown): FigmaNode[] {
  if (!json || typeof json !== "object") {
    throw new Error("Figma JSON 为空或不是对象");
  }
  const record = json as Record<string, unknown>;

  if (Array.isArray(json)) {
    return (json as unknown as FigmaNode[]).map((node) => normalizeNode(node));
  }

  // nodes 端点响应：{ nodes: { "1:2": { document } } }，取第一个 document
  if (record.nodes && typeof record.nodes === "object") {
    const docs = Object.values(record.nodes as Record<string, unknown>);
    const first = docs[0] as Record<string, unknown> | undefined;
    if (first?.document) {
      return [normalizeNode(first.document as FigmaNode)];
    }
  }

  // file 端点响应：{ document: { type: "CANVAS", children } }
  if (record.document && typeof record.document === "object") {
    const document = record.document as FigmaNode;
    if (document.type === "CANVAS" || document.type === "DOCUMENT") {
      return (document.children ?? []).map((node) => normalizeNode(node));
    }
    return [normalizeNode(document)];
  }

  // 裸节点树
  if (typeof record.type === "string") {
    return [normalizeNode(record as unknown as FigmaNode)];
  }

  throw new Error("无法识别的 Figma JSON（期望 file/nodes 端点响应或节点树）");
}

function normalizeNode(node: FigmaNode): FigmaNode {
  return {
    ...node,
    children: (node.children ?? []).map((child) => normalizeNode(child)),
  };
}

// ---------------------------------------------------------------------------
// 颜色 / 样式映射
// ---------------------------------------------------------------------------

/** Figma 0-1 颜色 → CSS 颜色字符串。 */
export function figmaColorToCss(color: FigmaColor | undefined, opacity?: number): string | null {
  if (!color) return null;
  const alpha = opacity ?? color.a ?? 1;
  const r = Math.round(Math.max(0, Math.min(1, color.r)) * 255);
  const g = Math.round(Math.max(0, Math.min(1, color.g)) * 255);
  const b = Math.round(Math.max(0, Math.min(1, color.b)) * 255);
  if (alpha >= 1) return `rgb(${r}, ${g}, ${b})`;
  return `rgba(${r}, ${g}, ${b}, ${Math.round(alpha * 100) / 100})`;
}

/** 取第一个可见的指定类型 fill。 */
export function firstVisibleFill(node: FigmaNode, type: string): FigmaFill | undefined {
  return (node.fills ?? []).find((fill) => fill.visible !== false && fill.type === type);
}

function solidBackground(node: FigmaNode): string | null {
  const fill = firstVisibleFill(node, "SOLID");
  return fill ? figmaColorToCss(fill.color, fill.opacity) : null;
}

function hasImageFill(node: FigmaNode): boolean {
  return firstVisibleFill(node, "IMAGE") !== undefined;
}

/** 名称启发式：instance/component 或矩形按名称映射 button/card。 */
function nameHint(node: FigmaNode): "button" | "card" | null {
  const name = (node.name ?? "").toLowerCase();
  if (/(button|btn|action)/.test(name)) return "button";
  if (/(card|panel|tile)/.test(name)) return "card";
  return null;
}

function borderRadiusOf(node: FigmaNode): string | null {
  if (typeof node.cornerRadius === "number" && node.cornerRadius > 0) {
    return `${node.cornerRadius}px`;
  }
  const radii = node.rectangleCornerRadii;
  if (Array.isArray(radii) && radii.some((r) => r > 0)) {
    return `${radii.map((r) => `${r}px`).join(" ")}`;
  }
  return null;
}

function sizeStyle(node: FigmaNode): StyleConfig {
  const style: StyleConfig = {};
  if (typeof node.width === "number") style.width = `${node.width}px`;
  if (typeof node.height === "number") style.height = `${node.height}px`;
  const radius = borderRadiusOf(node);
  if (radius) style.borderRadius = radius;
  return style;
}

/** auto-layout → 容器 props（direction/gap/padding/justify/align）。 */
function layoutProps(node: FigmaNode): Record<string, string> {
  const props: Record<string, string> = {};
  if (node.layoutMode === "HORIZONTAL") props.direction = "row";
  else if (node.layoutMode === "VERTICAL") props.direction = "column";

  const paddings = [node.paddingTop, node.paddingRight, node.paddingBottom, node.paddingLeft];
  if (paddings.some((p) => typeof p === "number" && p > 0)) {
    props.padding = `${node.paddingTop ?? 0}px ${node.paddingRight ?? 0}px ${node.paddingBottom ?? 0}px ${node.paddingLeft ?? 0}px`;
  }
  if (typeof node.itemSpacing === "number" && node.itemSpacing > 0) {
    props.gap = `${node.itemSpacing}px`;
  }
  if (node.primaryAxisAlignItems === "CENTER") props.justify = "center";
  else if (node.primaryAxisAlignItems === "MAX") props.justify = "flex-end";
  else if (node.primaryAxisAlignItems === "SPACE_BETWEEN") props.justify = "space-between";
  if (node.counterAxisAlignItems === "CENTER") props.align = "center";
  else if (node.counterAxisAlignItems === "MAX") props.align = "flex-end";
  return props;
}

// ---------------------------------------------------------------------------
// 节点 → 组件转换
// ---------------------------------------------------------------------------

/** 文本字号 → 组件 tag。 */
export function textTagFor(fontSize: number | undefined): "h1" | "h2" | "h3" | "p" {
  if (!fontSize) return "p";
  if (fontSize >= 28) return "h1";
  if (fontSize >= 22) return "h2";
  if (fontSize >= 18) return "h3";
  return "p";
}

function convertText(node: FigmaNode): ComponentNode {
  const fontSize = node.fontSize;
  const props: Record<string, unknown> = {
    content: node.characters ?? "",
    tag: textTagFor(fontSize),
  };
  if (fontSize) props.fontSize = `${fontSize}px`;
  const color = solidBackground(node);
  if (color) props.color = color;
  if (typeof node.fontWeight === "number" && node.fontWeight >= 600) props.bold = true;

  const style = sizeStyle(node);
  if (node.textAlignHorizontal === "CENTER") style.textAlign = "center";
  return { id: uuidv4(), type: "Text", props, style: Object.keys(style).length > 0 ? style : undefined };
}

function convertContainer(node: FigmaNode, stats: FigmaImportStats): ComponentNode {
  const hint = nameHint(node);
  const isHorizontal = node.layoutMode === "HORIZONTAL";
  const children = convertChildren(node, stats);

  if (hint === "button") {
    stats.buttons += 1;
    return {
      id: uuidv4(),
      type: "Button",
      props: { label: (node.name ?? "按钮").replace(/button|btn|action/gi, "").trim() || "按钮" },
      style: Object.keys(sizeStyle(node)).length > 0 ? sizeStyle(node) : undefined,
      children: undefined,
    };
  }

  if (hint === "card") {
    stats.cards += 1;
    const style = sizeStyle(node);
    const background = solidBackground(node);
    if (background) style.background = background;
    return {
      id: uuidv4(),
      type: "Card",
      props: { title: node.name ?? "卡片" },
      style: Object.keys(style).length > 0 ? style : undefined,
      children: children.length > 0 ? children : undefined,
    };
  }

  // 无子节点且仅有背景色的 FRAME/RECTANGLE → 纯装饰容器
  if (children.length === 0) {
    const style = sizeStyle(node);
    const background = solidBackground(node);
    if (background) style.background = background;
    return {
      id: uuidv4(),
      type: "Container",
      props: { ...layoutProps(node), ...(background ? { background } : {}) },
      style: Object.keys(style).length > 0 ? style : undefined,
      children: undefined,
    };
  }

  // 有子节点：HORIZONTAL auto-layout → Flex，其余 → Container
  stats.containers += 1;
  const type = isHorizontal ? "Flex" : "Container";
  const style = sizeStyle(node);
  const background = solidBackground(node);
  if (background) style.background = background;
  return {
    id: uuidv4(),
    type,
    props: { ...layoutProps(node), ...(background ? { background } : {}) },
    style: Object.keys(style).length > 0 ? style : undefined,
    children,
  };
}

function convertRectangle(node: FigmaNode, stats: FigmaImportStats): ComponentNode {
  const hint = nameHint(node);
  const style = sizeStyle(node);
  const background = solidBackground(node);

  if (hint === "button") {
    stats.buttons += 1;
    return {
      id: uuidv4(),
      type: "Button",
      props: { label: (node.name ?? "按钮").replace(/button|btn|action/gi, "").trim() || "按钮" },
      style: Object.keys(style).length > 0 ? style : undefined,
    };
  }

  if (hint === "card") {
    stats.cards += 1;
    if (background) style.background = background;
    return {
      id: uuidv4(),
      type: "Card",
      props: { title: node.name ?? "卡片" },
      style: Object.keys(style).length > 0 ? style : undefined,
    };
  }

  if (hasImageFill(node)) {
    stats.images += 1;
    return {
      id: uuidv4(),
      type: "Image",
      props: { src: "", alt: node.name ?? "图片", objectFit: "cover", width: style.width ?? "100%", height: style.height ?? "auto", rounded: Boolean(style.borderRadius) },
      style: Object.keys(style).length > 0 ? style : undefined,
    };
  }

  stats.containers += 1;
  if (background) style.background = background;
  return {
    id: uuidv4(),
    type: "Container",
    props: { ...(background ? { background } : {}) },
    style: Object.keys(style).length > 0 ? style : undefined,
  };
}

/** 递归转换子节点（未知类型降级：无子节点跳过，有子节点剥壳递归）。 */
function convertChildren(node: FigmaNode, stats: FigmaImportStats): ComponentNode[] {
  return (node.children ?? [])
    .map((child) => convertNode(child, stats))
    .filter((child): child is ComponentNode => child !== null);
}

function convertNode(node: FigmaNode, stats: FigmaImportStats): ComponentNode | null {
  switch (node.type) {
    case "TEXT": {
      stats.texts += 1;
      return convertText(node);
    }
    case "RECTANGLE": {
      return convertRectangle(node, stats);
    }
    case "FRAME":
    case "SECTION":
    case "GROUP":
    case "INSTANCE":
    case "COMPONENT":
    case "COMPONENT_SET":
    case "BOOLEAN_OPERATION":
    case "SHAPE_WITH_TEXT": {
      return convertContainer(node, stats);
    }
    default: {
      // 未知/纯图形类型：有子节点则剥壳递归，否则跳过
      const children = convertChildren(node, stats);
      if (children.length > 0) {
        return {
          id: uuidv4(),
          type: "Container",
          props: {},
          children,
        };
      }
      stats.skipped += 1;
      return null;
    }
  }
}

function emptyStats(): FigmaImportStats {
  return { containers: 0, texts: 0, cards: 0, images: 0, buttons: 0, skipped: 0 };
}

/** 转换整个 Figma JSON（file/nodes 端点响应或节点树）→ 组件树 + 统计。 */
export function importFigmaJson(json: unknown): FigmaImportResult {
  const roots = normalizeFigmaInput(json);
  if (roots.length === 0) {
    throw new Error("Figma JSON 中没有可转换的节点");
  }

  const stats = emptyStats();
  const warnings: string[] = [];
  const children: ComponentNode[] = [];

  for (const root of roots) {
    const converted = convertNode(root, stats);
    if (converted) children.push(converted);
    // convertNode 内部已对跳过的节点计数（default 分支）
  }

  const node: ComponentNode = {
    id: uuidv4(),
    type: "Container",
    props: { direction: "column", gap: "16px", padding: "16px" },
    children,
  };

  if (stats.images > 0) {
    warnings.push("IMAGE 型图片节点无法从 JSON 还原图片 URL，src 留空待手动填充");
  }
  if (stats.skipped > 0) {
    warnings.push(`${stats.skipped} 个不支持/纯图形节点已跳过（ELLIPSE/VECTOR/LINE 等）`);
  }
  return { node, stats, warnings };
}
